import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ArrowRight } from 'lucide-react';
import { formatChange, formatValue } from '@/shared/lib/format';
import type { ForecastChange } from '../api/schemas';
import { PageCite } from './PageCite';

type Props = { change: ForecastChange; index: number; onOpenPage: (page: number) => void };

const shown = (v: number | null, missing: string) => (v === null ? missing : formatValue(v));

/** "−2 · −1.3%", or null when the forecast is new or dropped. */
const moveLabel = (c: ForecastChange) => {
  if (c.previous === null || c.current === null) return null;
  return `${formatChange(c.previous, c.current)}${percent(c.previous, c.current)}`.replace(/-/g, '−');
};

const percent = (from: number, to: number) => (from === 0 ? '' : ` · ${to > from ? '+' : ''}${(((to - from) / Math.abs(from)) * 100).toFixed(1)}%`);

/** One revised forecast: before struck through, now in bold, and the move coloured by direction. */
export function ChangeCard({ change: c, index, onOpenPage }: Props) {
  const move = moveLabel(c);
  const up = (c.current ?? 0) > (c.previous ?? 0);
  return (
    <Stack sx={{ ...cardSx, animationDelay: `${index * 50}ms` }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.instrument}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', flexGrow: 1 }}>· {c.periodLabel}</Typography>
        {c.page ? <PageCite page={c.page} onOpen={onOpenPage} /> : null}
      </Stack>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mt: 0.75 }}>
        <Typography variant="body2" sx={{ color: 'text.disabled', textDecoration: 'line-through' }}>{shown(c.previous, 'New')}</Typography>
        <Box component="span" sx={{ display: 'flex', color: 'text.disabled' }} aria-hidden><ArrowRight size={13} /></Box>
        <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, flexGrow: 1 }}>{shown(c.current, 'Dropped')}</Typography>
        {move ? <Box component="span" sx={moveSx(up)}>{move}</Box> : null}
      </Stack>
    </Stack>
  );
}

const cardSx = {
  p: 1.5,
  border: 1,
  borderColor: 'divider',
  borderRadius: 1.25,
  bgcolor: 'background.paper',
  '@keyframes changeIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'none' } },
  animation: 'changeIn 240ms ease-out both',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const;

const moveSx = (up: boolean) => ({
  px: 0.75,
  py: 0.25,
  borderRadius: 0.75,
  fontSize: '0.75rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  color: up ? 'market.up' : 'market.down',
  bgcolor: up ? 'color-mix(in srgb, var(--mui-palette-market-up) 10%, transparent)' : 'color-mix(in srgb, var(--mui-palette-market-down) 10%, transparent)',
});
